/* eslint-disable no-shadow */
/* eslint-disable import/no-cycle */

import StorageService from '@/services/storage.service';

import Vue from 'vue';
import { router } from '@/router';
import UserService from '@/services/user.service';
import {
  AuthError, ForbiddenError, HttpError, PMSError, TooManyAttemptsError, ValidationError,
} from '@/errors';
import {
  apiEndpoint,
  defaultUserDateFormat,
  defaultUserNumberFormat,
  externalEngineHost,
  externalEngineTpl,
  langCodes,
  locales,
} from '@/shared';
import ApiService from '@/services/api.service';
import moment from 'moment';
import i18n from '@/i18n';
import { localize } from 'vee-validate';
import { cloneDeep, merge } from 'lodash';
import * as routerHelper from '@/helpers/router.helper';
import {
  defaultAvatarColors,
  defaultBackgroundColor,
  defaultBlackoutCalendarColors,
  defaultButtonColors,
  defaultCheckboxColors,
  defaultDropdownColors,
  defaultExplanationBoxColors,
  defaultHorizontalTabColors,
  defaultInputColors,
  defaultLeftSideNavColors,
  defaultMainColors,
  defaultModalColors,
  defaultRadioButtonColors,
  defaultTableColors,
  defaultTextColor,
  defaultTextColors,
  defaultToggleButtonColors,
  defaultTooltipColors,
  defaultWorkingAreaColors,
} from '@/shared/defaultColors';
import ColorCustomizeHelper from '@/helpers/colorCustomize.helper';

const endpoint = `${apiEndpoint}/user`;
const endpointUserAccount = `${apiEndpoint}/user-account`;
const propertyEndpoint = `${apiEndpoint}/property`;

const initialLang = StorageService.getLang();
ApiService.setLang(initialLang);
moment.locale(initialLang, { week: { dow: 1 } });
i18n.locale = initialLang;
localize(initialLang);
const initialProperty = StorageService.getProperty();
ApiService.setProperty(initialProperty);

const state = {
  user: StorageService.getUser(),
  emailResent: false,
  validationError: null,
  rateLimitError: null,
  pending: true,
  sessionExpired: false,
  lang: initialLang,
  property: initialProperty,
  propertyRoomDBData: null,
  propertiesPending: false,
  propertiesLoaded: false,
  pages: null,
  groupUpdatePending: false,
  hostInfo: null,
  propertyId: null,
  tabData: null,
  propertiesGroup: null,
  propertyIsDeleted: false,
  mainLanguage: 'en',
  error: null,
  currentProperty: null,
  userProperties: [],
  typesenseUpdateLoading: false,
  currentPropertyPending: false,
  userFollowers: [],
  userFollowees: [],
  supportedLanguages: [],
  autoTranslatedLanguages: [],
  userPropertySetting: null,
  userPropertySettingPending: null,
  offersData: {},
  identifiers: [],
  batchPropertyCreateLogs: [],
};

const getters = {
  loggedIn: (state) => state.user != null,
  hostLoaded: (state) => state.hostInfo != null,
  hostInfo: (state, getters) => (getters.hostLoaded ? state.hostInfo : {}),
  user: (state) => state.user,
  userID: (state, getters) => (getters.loggedIn ? state.user.id : null),
  settings: (state, getters) => (getters.user && state.user.configuration ? state.user.configuration : {}),
  rdbIsMaster: (state) => !!state.currentProperty?.landing_property?.roomdb_is_master,
  error: (state) => state.error,
  currentProperty: (state) => state.currentProperty,
  currentPropertyPending: (state) => state.currentPropertyPending,
  numberFormat: (state, getters) => {
    // eslint-disable-next-line camelcase
    const { numberFormat } = getters.settings;
    return numberFormat || defaultUserNumberFormat;
  },
  dateFormat: (state, getters) => {
    // eslint-disable-next-line camelcase
    const { dateFormat } = getters.settings;
    return dateFormat || defaultUserDateFormat;
  },
  isAgentUser: (state, getters) => (getters.loggedIn ? state.user.of_agent : false),
  isAgentDomain: (state, getters) => (getters.hostLoaded ? getters.hostInfo.agent : false),
  engineURL: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    const host = group?.b_domain ?? hostInfo?.b_domain ?? externalEngineHost;
    return externalEngineTpl.replace('{host}', host);
  },
  userOTL: (state, getters) => (getters.isAgentUser ? state.user.otl : null),
  sessionExpired: (state) => state.sessionExpired,
  emailResent: (state) => state.emailResent,
  pdFilled: (state, getters) => !!(getters.loggedIn && state.user.pdFillTime),
  cdFilled: (state, getters) => !!(getters.loggedIn && state.user?.cdFillTime),
  setupComplete(state, getters) {
    if (!getters.loggedIn) {
      return true;
    }
    if (!state.user.of_agent) {
      return state.user.setupTime;
    }
    const { property } = getters;
    if (property == null || property.id == null || property.is_default) {
      return true;
    }
    return property.agent_setup_complete;
  },
  setupStep(state, getters) {
    if (!getters.loggedIn) {
      return 0;
    }
    if (!state.user.of_agent) {
      return state.user.setupStep || 0;
    }
    const { property } = getters;
    if (property == null || property.id == null) {
      return 0;
    }
    return Math.max(property.agent_setup_step || 0, 2);
  },
  requiredFilled: (state, getters) => getters.pdFilled && getters.cdFilled,
  isAdmin: (state, getters) => getters.loggedIn && state.user.admin,
  isSuperAdmin: (state, getters) => getters.loggedIn && state.user.isSuperAdmin,
  hasProfile: (state, getters) => getters.loggedIn && state.user.profile != null,
  hasProperties: (state, getters) => getters.loggedIn && state.user.nb_properties > 0,
  multipleProperties: (state, getters) => getters.hasProperties && state.user.nb_properties > 1,
  // properties: (state, getters) => (getters.hasProperties ? state.user.properties : []),
  propertyName: (state, getters) => {
    let result;
    if (getters.hasProperties) {
      result = getters.property ? getters.property.name : '';
    } else {
      result = '';
    }
    return result;
  },
  propertyIsDeleted: (state, getters) => {
    let result;
    if (getters.hasProperties) {
      result = getters.property ? getters.property.isDeleted : '';
    } else {
      result = '';
    }
    return result;
  },
  propertyID: (state, getters) => {
    let result;
    if (getters.hasProperties) {
      result = getters.property ? getters.property.id : '';
    } else {
      result = '';
    }
    return result;
  },
  propertyRoomDBID: (state, getters) => {
    let result;
    if (getters.hasProperties) {
      result = getters.property ? getters.property.roomdb_id : '';
    } else {
      result = '';
    }
    return result;
  },
  propertyIsGroup: (state, getters) => {
    let result;
    if (getters.hasProperties) {
      result = !!(getters.property && getters.property.is_group);
    } else {
      result = '';
    }
    return result;
  },
  propertyPages: (state, getters) => (getters.hasProperties ? getters.property.pages : false),
  currency: (state, getters) => (getters.hasProperties && getters.property.currency_code
    ? getters.property.currency_code : 'EUR'),
  pending: (state) => state.pending,
  validationError: (state) => state.validationError,
  rateLimitError: (state) => state.rateLimitError,
  lang: (state) => {
    let l = locales.find(({ code }) => code === state.lang);
    if (l == null) {
      l = locales.find(({ code }) => code === 'en');
    }
    return l;
  },
  property: (state, getters) => {
    if (!getters.currentProperty) return null;
    // if (state.property == null) return {};
    return getters.currentProperty.landing_property;
  },
  propertyRoomDBData: (state) => state.propertyRoomDBData ?? {},
  group: (state, getters) => {
    if (!getters.loggedIn) return null;
    if (getters.property && Object.keys(getters.property).length > 0) {
      return getters.property.group;
      /* return state.user.groups.find((g) => g.id === getters.property.group_id); */
    }
    return {};
  },
  groupProperty: (state, getters) => {
    if (!getters.loggedIn) return null;
    return getters.currentProperty.propertyGroup;
  },
  propertyIsDefault: (state, getters) => getters.property && getters.property.is_default,
  pageAllowed: (state, getters) => (pages) => {
    const current = getters.property;
    const currentPages = !Array.isArray(pages) ? Array(pages) : pages;
    if (current == null || current.perms == null) return false;

    return Array.isArray(current.perms) && currentPages.some((page) => current.perms.includes(page));
  },
  allowedPages: (state, getters) => {
    const current = getters.property;
    if (current == null || current.perms == null) return [];
    return [...current.perms];
  },
  allowedUserPages: (state, getters) => {
    const { user } = getters;
    const { property } = getters;
    if (!getters.loggedIn || !user.roles || user.roles === []) return [];
    return user.roles
      .reduce((acc, curr) => (
        curr.pages && curr.pages.length > 0 && curr.property_id === property.id
          ? Array.from(new Set([...acc, ...curr.pages]))
          : acc), []);
  },
  userRoles: (state, getters, rootState, rootGetters) => {
    if (!getters.loggedIn) return [];
    const allUsers = rootGetters['users/users'];
    const allRoles = rootGetters['roles/roles'];
    if (allUsers !== [] && allRoles !== [] && getters.userID !== null) {
      const anotherCurrentUserObject = allUsers.find((user) => user.id === getters.userID);
      return anotherCurrentUserObject === undefined
        ? []
        : allRoles
          .filter((item) => anotherCurrentUserObject.all_roles
            .reduce((acc, curr) => [...acc, curr.id], []).includes(item.id));
    }
    return [];
  },
  currentAvatar: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.user) {
      return getters.user.avatar;
    }
    return group?.avatar ?? hostInfo?.avatar;
  },
  currentLogo: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.logoUrl) {
      return getters.currentProperty.propertyGroup.logoUrl;
    }
    return group?.logo ?? hostInfo?.logo;
  },
  currentFavicon: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.landing_property?.favicon) {
      return getters.currentProperty.landing_property.favicon;
    }
    if (getters.currentProperty?.propertyGroup?.favicon) {
      return getters.currentProperty.propertyGroup.favicon;
    }
    return group?.favicon ?? hostInfo?.favicon;
  },
  activePropertyID: (state, getters) => getters.currentProperty?.landing_property?.roomdb_id || null,
  currentColorSchema: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.color_schema) {
      return getters.currentProperty.propertyGroup.style.color_schema;
    }
    return group?.style?.color_schema ?? hostInfo.color_schema ?? defaultBackgroundColor;
  },
  currentColorFont: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.color_font) {
      return getters.currentProperty.propertyGroup.style.color_font;
    }
    return group?.style?.color_font ?? hostInfo.color_font ?? defaultTextColors.white;
  },
  mainColor: (state, getters) => {
    const { hostInfo } = getters;
    const {
      r, g, b, a,
    } = hostInfo.btn_background.rgba;
    return `color: rgba(${r}, ${g}, ${b}, ${a}) !important`;
  },
  borderColor: (state, getters) => {
    const { hostInfo } = getters;
    const {
      r, g, b, a,
    } = hostInfo.btn_background.rgba;
    return `border: 1px solid rgba(${r}, ${g}, ${b}, ${a}) !important`;
  },
  currentDomainPageTitle: (state, getters) => {
    const { hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.pageTitle) {
      return getters.currentProperty.propertyGroup.pageTitle;
    }
    return hostInfo.pageTitle;
  },
  workingArea: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.working_area) {
      // eslint-disable-next-line max-len
      return merge(cloneDeep(defaultWorkingAreaColors), cloneDeep(getters.currentProperty.propertyGroup.style.working_area));
    }
    return group?.style?.working_area ?? hostInfo.working_area ?? defaultWorkingAreaColors;
  },
  mainColors: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.main) {
      return getters.currentProperty.propertyGroup.style.main;
    }
    return group?.style?.main ?? hostInfo.main ?? defaultMainColors;
  },
  horizontalLineColor: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.horizontal_line_color) {
      return getters.currentProperty.propertyGroup.style.horizontal_line_color;
    }
    return group?.style?.horizontal_line_color ?? hostInfo.horizontal_line_color ?? defaultTextColor;
  },
  dropdown: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.dropdown) {
      return merge(cloneDeep(defaultDropdownColors), cloneDeep(getters.currentProperty.propertyGroup.style.dropdown));
    }
    return group?.style?.dropdown ?? hostInfo.dropdown ?? defaultDropdownColors;
  },
  leftSideNav: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.left_sidenav) {
      return getters.currentProperty.propertyGroup.style.left_sidenav;
    }
    return group?.style?.left_sidenav ?? hostInfo.left_sidenav ?? defaultLeftSideNavColors;
  },
  toggleButton: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.toggle_button) {
      return getters.currentProperty.propertyGroup.style.toggle_button;
    }
    return group?.style?.toggle_button ?? hostInfo.toggle_button ?? defaultToggleButtonColors;
  },
  explanationBox: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.explanation_box) {
      // eslint-disable-next-line max-len
      return merge(cloneDeep(defaultExplanationBoxColors), cloneDeep(getters.currentProperty.propertyGroup.style.explanation_box));
    }
    return group?.style?.explanation_box ?? hostInfo.explanation_box ?? defaultExplanationBoxColors;
  },
  avatar: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.avatar) {
      return getters.currentProperty.propertyGroup.style.avatar;
    }
    return group?.style?.avatar ?? hostInfo.avatar ?? defaultAvatarColors;
  },
  table: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.table) {
      return getters.currentProperty.propertyGroup.style.table;
    }
    return group?.style?.table ?? hostInfo.table ?? defaultTableColors;
  },
  propertiesLoaded: (state) => !state.propertiesPending,
  propertiesPending: (state) => state.propertiesPending,
  radioButton: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.radio_button) {
      return getters.currentProperty.propertyGroup.style.radio_button;
    }
    return group?.style?.radio_button ?? hostInfo.radio_button ?? defaultRadioButtonColors;
  },
  input: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.input) {
      return getters.currentProperty.propertyGroup.style.input;
    }
    return group?.style?.input ?? hostInfo.input ?? defaultInputColors;
  },
  button: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.button) {
      return getters.currentProperty.propertyGroup.style.button;
    }
    return group?.style?.button ?? hostInfo.button ?? defaultButtonColors;
  },
  checkbox: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.checkbox) {
      return getters.currentProperty.propertyGroup.style.checkbox;
    }
    return group?.style?.checkbox ?? hostInfo.checkbox ?? defaultCheckboxColors;
  },
  modal: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.modal) {
      return getters.currentProperty.propertyGroup.style.modal;
    }
    return group?.style?.modal ?? hostInfo.modal ?? defaultModalColors;
  },
  horizontalTab: (state, getters) => {
    const { group, hostInfo } = getters;
    // eslint-disable-next-line camelcase
    if (getters.currentProperty?.propertyGroup?.style?.horizontal_tab) {
      return getters.currentProperty.propertyGroup.style.horizontal_tab;
    }
    return group?.style?.horizontal_tab ?? hostInfo.horizontal_tab ?? defaultHorizontalTabColors;
  },
  tooltip: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.tooltip) {
      return getters.currentProperty.propertyGroup.style.tooltip;
    }
    return group?.style?.tooltip ?? hostInfo.tooltip ?? defaultTooltipColors;
  },
  blackoutCalendar: (state, getters) => {
    const { group, hostInfo } = getters;
    if (getters.currentProperty?.propertyGroup?.style?.blackout_calendar) {
      // eslint-disable-next-line max-len
      return merge(cloneDeep(defaultBlackoutCalendarColors), cloneDeep(getters.currentProperty.propertyGroup.style.blackout_calendar));
    }
    return group?.style?.blackout_calendar ?? hostInfo.blackout_calendar ?? defaultBlackoutCalendarColors;
  },
  propertyAlias: (state, getters) => {
    const { property } = getters;
    if (getters.user && property) {
      return property.alias_to ?? null;
    }
    return null;
  },
  userProperties: (state, getters) => {
    const { userProperties } = getters;
    return userProperties;
  },
  userPropertySetting: (state, getters) => getters.currentProperty?.landing_property?.settings,
  userPropertySettingPending: (state) => state.userPropertySettingPending,
  identifiers: (state) => state.identifiers,
  propertyCreateBatchLogs: (state) => state.batchPropertyCreateLogs,
};

const mutations = {
  clearErrors(state) {
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
  },
  user(state, user) {
    if (user) {
      StorageService.setUser(user);
    } else {
      StorageService.removeUser();
    }
    state.user = user;
    state.sessionExpired = false;
    state.pending = false;
  },
  currentProperty(state, property) {
    state.currentProperty = property;
  },
  magicLink(state, user) {
    state.user.magicLink = user.magicLink;
  },
  info(state, info) {
    state.hostInfo = info ?? {};
  },
  sessionExpired(state) {
    if (state.user != null) {
      state.sessionExpired = true;
    }
  },
  emailResent(state, ok) {
    state.emailResent = !!ok;
  },
  beforeRequest(state) {
    state.validationError = null;
    state.rateLimitError = null;
    state.error = null;
    state.pending = true;
  },
  requestComplete(state) {
    state.pending = false;
  },
  validationError(state, error) {
    state.validationError = error;
    state.pending = false;
  },
  rateLimitError(state, error) {
    state.pending = false;
    state.rateLimitError = error;
  },
  currency(state, currency) {
    state.user.profile.currency = currency;
  },
  setup(state, data) {
    Object.keys(data).forEach((k) => {
      Vue.set(state.user, k, data[k]);
    });
    state.pending = false;
  },
  propertySetup(state, data) {
    // const property = state.user.properties.find(({ id }) => id === data.id);
    if (state.currentProperty.landing_property != null) {
      state.currentProperty.landing_property.agent_setup_complete = data.agent_setup_complete;
      state.currentProperty.landing_property.agent_setup_step = data.agent_setup_step;
    }
    state.pending = false;
  },
  setPropertyGroupStyle(state, data) {
    state.currentProperty.propertyGroup.style = data;
  },
  setLang(state, locale) {
    if (state.lang === locale) return;
    const l = locales.find(({ code }) => code === locale);
    if (l !== null) {
      StorageService.setLang(locale);
      state.lang = locale;
      ApiService.setLang(locale);
      moment.locale(locale, { week: { dow: 1 } });
      i18n.locale = locale;
      localize(locale);
    }
  },
  setProperty(state) {
    if (state.currentProperty == null) {
      state.property = null;
      ApiService.setProperty();
      return;
    }
    const id = state.currentProperty?.landing_property?.id || null;
    StorageService.setProperty(id);
    state.property = id;
    ApiService.setProperty(id);
  },
  beforeCurrentPropertyRequest(state) {
    state.currentPropertyPending = true;
  },
  afterCurrentPropertyRequest(state) {
    state.currentPropertyPending = false;
  },
  beforePropertiesRequest(state) {
    state.propertiesPending = true;
  },
  afterPropertiesRequest(state) {
    state.propertiesPending = false;
  },
  properties(state) {
    state.propertiesPending = false;
    state.propertiesLoaded = true;
  },
  addProperty(state) {
    state.propertiesPending = false;
  },
  propertyRoomDBData(state, data) {
    state.propertyRoomDBData = data;
    state.currentProperty.landing_property.forTesting = data.forTesting;
    state.currentProperty.landing_property.roomdb_is_master = data.roomdb_is_master;
    state.currentProperty.landing_property.isDeleted = data.isDeleted;
    state.currentProperty.landing_property.alias_to = data.alias_to;
  },
  updateProperty(state) {
    state.propertiesPending = false;
  },
  updatePropertyIsDeleted(state, value) {
    state.currentProperty.landing_property.isDeleted = Boolean(value);
  },
  patchProperty(state) {
    state.propertiesPending = false;
  },
  updateStatus(state, data) {
    Vue.set(state.currentProperty.landing_property, 'active', data.active);
  },
  updatePropertyAlias(state, value) {
    state.currentProperty.landing_property.alias_to = value;
  },
  deleteProperty(state) {
    state.propertiesPending = false;
  },
  beforeGroupRequest(state) {
    state.groupUpdatePending = true;
  },
  afterGroupRequest(state, { group = null } = {}) {
    if (group != null) {
      if (state.property && Object.keys(state.property).length > 0) state.property.group = group;
      // TODO
      // Remove it after testing it correctly or
      // removing relastionship between User and Group
      state.user.groups = state.user.groups.map((g) => {
        if (g.id === group?.id) return group;
        return g;
      });
    }
    state.groupUpdatePending = false;
  },
  beforePropertyGroupRequest(state) {
    state.pending = true;
  },
  afterPropertyGroupRequest(state, data) {
    state.pending = false;
    state.propertiesGroup = data;
  },
  updatePropertyStatus(state, active) {
    state.currentProperty.landing_property.active = active;
  },
  propertyId(state, id) {
    state.propertyId = id;
  },
  setMainLanguage(state, { lang, supportedLanguages, autoTranslatedLanguages }) {
    state.mainLanguage = langCodes.includes(lang) ? lang : 'en';
    const filteredSupported = supportedLanguages.filter((code) => langCodes.includes(code));
    state.supportedLanguages = filteredSupported;
    state.autoTranslatedLanguages = autoTranslatedLanguages.filter((code) => filteredSupported.includes(code));
  },
  error(state, data) {
    state.pending = false;
    state.error = data;
  },
  updatePropertyObject(state, { item, value }) {
    if (state.propertyRoomDBData
      && Object.prototype.hasOwnProperty.call(state.propertyRoomDBData, item)) {
      state.propertyRoomDBData[item] = value;
    }
  },
  updateLandingPropertyObject(state, { item, value }) {
    if (state.currentProperty && state.currentProperty.landing_property
      && Object.prototype.hasOwnProperty.call(state.currentProperty.landing_property, item)) {
      state.currentProperty.landing_property[item] = value;
    }
  },
  beforeUserProperties(state) {
    state.pending = true;
    state.userProperties = [];
  },
  afterUserProperties(state, data) {
    state.pending = false;
    state.userProperties = data;
  },
  updateRoomDBForTesting(state, value) {
    state.propertyForTesting = Boolean(value);
  },
  beforeTypesenseUpdate(state) {
    state.typesenseUpdateLoading = true;
  },
  afterTypesenseUpdate(state) {
    state.typesenseUpdateLoading = false;
  },
  beforeUserFollowers(state) {
    state.pending = true;
    state.userFollowers = [];
  },
  afterUserFollowers(state, data) {
    state.userFollowers = data;
    state.pending = false;
  },
  beforeUserFollowees(state) {
    state.pending = true;
    state.userFollowees = [];
  },
  afterUserFollowees(state, data) {
    state.userFollowees = data;
    state.pending = false;
  },
  beforeCreateFollower(state) {
    state.pending = true;
  },
  afterCreateFollower(state) {
    state.pending = false;
  },
  setUpdateUserPropertySettingRequest(state, data) {
    state.userPropertySettingPending = data;
  },
  beforeOffersData(state) {
    state.pending = true;
  },
  afterOffersData(state, data) {
    state.offersData = data;
    state.pending = false;
  },
  identifiers(state, data) {
    state.identifiers = data;
  },
  beforePropertyBatchLogs(state) {
    state.batchPropertyCreateLogs = [];
    state.pending = true;
  },
  afterPropertyBatchLogs(state) {
    state.pending = false;
  },
  addBatchPropertyLog(state, data) {
    state.batchPropertyCreateLogs.push(data);
  },
  resetBatchPropertyLogs(state) {
    state.batchPropertyCreateLogs = [];
  },
};

function handleError(commit, error) {
  if (error instanceof AuthError) {
    commit('loginAuthError', {
      code: error.errorCode,
      message: error.message,
    });
  } else if (error instanceof ValidationError) {
    commit('validationError', error);
  } else if (error instanceof TooManyAttemptsError) {
    commit('rateLimitError', error);
  } else if (error instanceof ForbiddenError) {
    throw error;
  } else if (error instanceof PMSError) {
    throw error;
  } else {
    let errorMessage = error.message;
    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    commit('error', errorMessage);
    throw error;
  }
}

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 403: // PMSError
      err = new ForbiddenError(status, data.message);
      break;
    case 422:
      err = new ValidationError(status, data.message, data.errors);
      commit('validationError', err);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  throw err;
}

const actions = {
  async getUser({ state, dispatch, commit }, { redirect = true, masterdata = false } = {}) {
    const wasLoggedIn = state.user != null;
    try {
      const user = await UserService.getUser(masterdata);
      const property = StorageService.getProperty() ?? user.landingPagePropertyId;
      if (property) {
        await dispatch('getCurrentProperty', { id: property });
      }
      await commit('user', user);
      if (!wasLoggedIn && redirect) {
        await ColorCustomizeHelper.setPropertyGroupColors();
        await router.push(router.history.current.query.redirect || { name: routerHelper.dashboardRouteName });
      }
    } catch (e) {
      if (wasLoggedIn) {
        commit('user', null);
        commit('currentProperty', null);
        if (redirect) {
          await router.push({ name: routerHelper.loginRouteName });
        }
      }
    }
  },
  async getGroupForColorPreview({ commit }, { groupId = null } = {}) {
    commit('beforePropertiesRequest');
    try {
      const { data } = await ApiService.get(`${endpoint}/property/group/${groupId}`);
      commit('setPropertyGroupStyle', data.style);
      return data;
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  async getPropertyGroupColors({ commit }, { groupId = null } = {}) {
    commit('beforeRequest');
    try {
      const { data } = await ApiService.get(`${endpoint}/property/group/${groupId}`);
      commit('requestComplete');
      return data;
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
      return null;
    }
  },
  async getInfo({ commit }) {
    commit('beforeRequest');
    const info = await UserService.getInfo();
    await commit('info', info);
    commit('requestComplete');
  },
  async resendEmail({ commit }) {
    commit('beforeRequest');
    try {
      const ok = await UserService.resendEmail();
      commit('emailResent', ok);
      commit('requestComplete');
    } catch (e) {
      commit('emailResent', false);
      handleError(commit, e);
    }
  },
  async updateProfile({ commit, dispatch, getters }, data) {
    commit('beforeRequest');
    const needRedirect = !getters.requiredFilled;
    try {
      const user = await UserService.updateProfile(data);
      const userLandingProperty = user.landingPagePropertyId;
      if (userLandingProperty) {
        await dispatch('user/getCurrentProperty', { id: userLandingProperty }, { root: true });
      }
      commit('user', user);
      commit('requestComplete');
      if (needRedirect && getters.requiredFilled) {
        if (getters.setupComplete) {
          await router.push({ name: routerHelper.dashboardRouteName });
        } else {
          await router.push({ name: routerHelper.setupRouteName });
        }
      }
    } catch (e) {
      commit('requestComplete');
      handleError(commit, e);
    }
  },
  async sendRenewMagicLink({ commit }) {
    try {
      commit('beforeRequest');
      const user = await UserService.sendRenewMagicLink();
      commit('magicLink', user);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },
  async getNewMagicLink({ commit }) {
    let link = null;
    try {
      commit('beforeRequest');
      link = await UserService.getNewMagicLink();
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
    return link;
  },
  async updateProfileData({ commit }, data) {
    commit('beforeRequest');
    try {
      let payload;
      if (
        (data.avatar == null || data.avatar.upload == null)
      ) {
        payload = { ...data, _method: 'put' };
      } else {
        payload = new FormData();
        payload.append('_method', 'put');
        payload.appendFromObject(data);
      }
      const { data: user } = await ApiService.post(`${endpoint}`, payload);
      commit('user', user);
      commit('requestComplete');
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },
  async updateSetup({ commit, getters }, payload) {
    commit('beforeRequest');
    const needRedirect = !getters.setupComplete;
    const agent = getters.isAgentUser;
    try {
      const data = await UserService.setupStep(payload);
      if (!agent) {
        commit('setup', data);
      } else {
        commit('propertySetup', data);
      }
      if (needRedirect && getters.setupComplete) {
        await router.push({ name: routerHelper.dashboardRouteName });
      }
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
    }
  },
  async getProperties({ commit }, payload) {
    const page = payload?.page || 1;
    const perPage = payload?.perPage || 10;
    const filter = payload?.filter ? payload.filter : '';
    const order = payload?.order ? payload.order : 'createdAt';
    const sort = payload?.sort ? payload.sort : 'desc';
    const createdFrom = payload?.createdFrom ?? '';
    const createdTo = payload?.createdTo ?? '';
    const updatedFrom = payload?.updatedFrom ?? '';
    const updatedTo = payload?.updatedTo ?? '';
    const search = payload?.search ?? '';
    const searchKey = payload?.searchKey ?? '';
    const params = {
      page,
      perPage,
      filter,
      order,
      sort,
      createdFrom,
      createdTo,
      updatedFrom,
      updatedTo,
      search,
      searchKey,
    };
    try {
      commit('beforePropertiesRequest');
      const { data } = await ApiService.post(`${endpoint}/properties/list`, params);
      commit('properties');
      commit('afterPropertiesRequest');
      return data;
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
      return {};
    }
  },
  async exportProperties({ commit }, payload) {
    const page = payload?.page || 1;
    const perPage = 1000;
    const filter = payload?.filter ? payload.filter : '';
    const order = payload?.order ? payload.order : 'createdAt';
    const sort = payload?.sort ? payload.sort : 'desc';
    const createdFrom = payload?.createdFrom ?? '';
    const createdTo = payload?.createdTo ?? '';
    const updatedFrom = payload?.updatedFrom ?? '';
    const updatedTo = payload?.updatedTo ?? '';
    const search = payload?.search ?? '';
    const searchKey = payload?.searchKey ?? '';
    const params = {
      page,
      perPage,
      filter,
      order,
      sort,
      createdFrom,
      createdTo,
      updatedFrom,
      updatedTo,
      search,
      searchKey,
    };
    try {
      const { data } = await ApiService.post(`${endpoint}/properties/list`, params);
      return data;
    } catch (error) {
      handleApiError(commit, error);
      return {};
    }
  },
  async findProperties({ commit }, payload) {
    try {
      const { data } = await ApiService.post(`${endpoint}/properties/find`, payload);
      return data;
    } catch (error) {
      handleApiError(commit, error);
      return {};
    }
  },
  async getProperty({ commit, getters }, id = null) {
    commit('beforePropertiesRequest');
    try {
      const { data } = await ApiService.get(`${propertyEndpoint}/master-data/info/${id || getters.property.id}`);
      commit('updateProperty');
      commit('propertyRoomDBData', data);
      commit('settings/setDefaultCancellationRules', data?.showCultSwitchDefaultCancellationRules, { root: true });
      return data;
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  async getRoomDBPropertyById({ commit }, id) {
    try {
      const { data } = await ApiService.get(`${propertyEndpoint}/master-data/info/${id}`);
      return data;
    } catch (error) {
      handleApiError(commit, error);
      return null;
    }
  },
  async getCurrentProperty({ commit, dispatch }, { id = null, force = null, access = null } = {}) {
    try {
      commit('beforeCurrentPropertyRequest');
      const propertyID = StorageService.getProperty();
      const currentPropertyId = id || getters.user.landingPagePropertyId || propertyID;
      let url = `${endpoint}/property/${currentPropertyId}`;
      const params = [];
      if (force) params.push('force');
      if (access) params.push('access');
      if (params.length) {
        url += `?${params.join('&')}`;
      }
      const data = await ApiService.get(url);
      const currentProperty = data.data;
      commit('currentProperty', currentProperty);
      const landingProperty = currentProperty?.landing_property;
      if (landingProperty) {
        commit('setProperty');
      }
      commit('afterCurrentPropertyRequest');
      if (force) {
        dispatch('getUserPropertySetting');
      }
      return data.data;
    } catch (error) {
      handleApiError(commit, error);
      return null;
    }
  },
  async createProperty({ commit }, payload) {
    commit('beforePropertiesRequest');
    try {
      const { data: property } = await ApiService.post(`${endpoint}/properties`, payload);
      commit('addProperty');
      return property;
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
      return null;
    }
  },
  resetBeforePropertyBatchLogs({ commit }) {
    commit('resetBatchPropertyLogs');
  },
  addCancelLog({ commit }, data) {
    commit('addBatchPropertyLog', data);
  },
  async createPropertyBatch({ commit }, { formData, signal }) {
    commit('beforePropertyBatchLogs');
    const response = await fetch(`${endpoint}/properties/batch`, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      handleApiError(commit, {
        response: {
          status: 422,
          data:
            { message: errorData.message },
        },
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const logs = [];

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read();
          if (done) break;

          const text = decoder.decode(value, { stream: true }).trim();
          if (!text) return;

          let messages;
          try {
            messages = text.split('\n').map((line) => JSON.parse(line));
          } catch (parseError) {
            /* eslint-disable no-console */
            console.error('JSON Parse Error:', parseError, text);
          }

          messages.forEach((msg) => {
            logs.push(msg.message);
            commit('addBatchPropertyLog', { message: msg.message, status: msg.lineStatus });
          });

          if (messages.some((m) => m.requestStatus === 'completed')) {
            resolve(logs);
            return;
          }
        }
        commit('afterPropertyBatchLogs');
        resolve(logs);
      } catch (error) {
        commit('afterPropertyBatchLogs');
        reject(error);
      }
    });
  },
  async updateProperty({ commit, getters }, dataDB) {
    commit('beforePropertiesRequest');
    commit('clearErrors');
    try {
      const { id } = dataDB;
      let payload;
      if (dataDB.logo == null || dataDB.logo.upload == null) {
        payload = { ...dataDB };
      } else {
        payload = new FormData();
        payload.appendFromObject(dataDB);
      }
      delete payload.id;
      const { data } = await ApiService.post(`${propertyEndpoint}/master-data/info/${id || getters.property.id}`, payload);
      commit('updateProperty');
      commit('currentProperty', { landing_property: data.landing_property, propertyGroup: data.propertyGroup });
      commit('propertyRoomDBData', data.roomdb_property);
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async updatePropertyLegal({ commit }, payload) {
    commit('beforePropertiesRequest');
    try {
      await ApiService.put(`${propertyEndpoint}/master-data/legal`, payload);
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async togglePropertyStatus({ commit }, data) {
    commit('beforePropertiesRequest');
    try {
      const { propertyId } = data;
      const payload = { ...data };
      delete payload.propertyId;
      const { data: log } = await ApiService.patch(`${endpoint}/properties/${propertyId}`, payload);
      commit('patchProperty');
      commit('logs/insert', { key: 'booking', data: log }, { root: true });
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async togglePropertyBookingStatus({ commit, getters }, data) {
    commit('beforePropertiesRequest');
    try {
      const { propertyId } = data;
      const payload = { ...data };
      delete payload.propertyId;
      const { data: log } = await ApiService.put(`${propertyEndpoint}/booking-status/change/${propertyId || getters.property.id}`, payload);
      if (!propertyId) {
        commit('updateStatus', payload);
      }
      commit('patchProperty');
      commit('logs/insert', { key: 'booking', log }, { root: true });
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async updateGroup({ commit, getters }, data) {
    commit('beforeGroupRequest');
    try {
      let payload;
      if (data.logo == null || data.logo.upload == null) {
        payload = { ...data, _method: 'put' };
      } else {
        payload = new FormData();
        payload.append('_method', 'put');
        payload.appendFromObject(data);
      }
      const { data: group } = await ApiService.post(`${endpoint}/group/${getters.group.id}`, payload);
      commit('afterGroupRequest', { group });
      return true;
    } catch (error) {
      commit('afterGroupRequest');
      handleApiError(commit, error);
      return false;
    }
  },
  async getStaticDataAttributes({ getters }, data) {
    await (getters.property.roomdb_id);
    const res = await ApiService.post(`${endpoint}/properties/roomdbData/staticData`, { roomdbId: data.roomdbId });
    if (res.data.status === 'fail') {
      return null;
    }

    return res.data;
  },
  async getRoomDBData({ getters }, data) {
    await (getters.property.roomdb_id);
    const res = await ApiService.get(`${propertyEndpoint}/master-data/target-groups/${data.pageName}/${data.tabName}`);
    if (res.data.status === 'fail') {
      return null;
    }
    return res.data;
  },
  async getIdentifierSources() {
    const res = await ApiService.get(`${propertyEndpoint}/master-data/identifiers/sources`);
    return res.data.result;
  },
  async getPropertyIdentifiers({ commit }) {
    const res = await ApiService.get(`${propertyEndpoint}/master-data/identifiers`);
    const data = res.data.result;
    commit('identifiers', data);
    return data;
  },
  async updatePropertyIdentifiers({ commit }, payload) {
    commit('beforeRequest');
    try {
      const req = {
        propertyId: payload.id,
        identifiers: payload.ids.map((item) => (
          {
            identifier: item.value || '',
            sourceId: item.id,
            url: item.url || '',
          }
        )),
      };
      const res = await ApiService.post(`${propertyEndpoint}/master-data/identifiers`, req);
      const identifiers = res.data?.data?.result;
      commit('identifiers', identifiers);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('requestComplete');
    }
    return null;
  },
  async addPropertyAlias({ commit }, payload) {
    commit('beforeRequest');
    try {
      await ApiService.post(`${propertyEndpoint}/master-data/identifiers/alias`, payload);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('requestComplete');
    }
    return null;
  },
  async deletePropertyAlias({ commit }) {
    commit('beforeRequest');
    try {
      await ApiService.delete(`${propertyEndpoint}/master-data/identifiers/alias`);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('requestComplete');
    }
    return null;
  },
  async fetchPropertiesGroup({ commit }) {
    try {
      commit('beforePropertyGroupRequest');
      const { data } = await ApiService.get(`${propertyEndpoint}/master-data/groups`);
      commit('afterPropertyGroupRequest', data);
      return true;
    } catch (error) {
      commit('afterPropertyGroupRequest');
      handleApiError(commit, error);
      return false;
    }
  },
  async getPropertyGroup({ getters }) {
    try {
      const response = await ApiService.get(`${endpoint}/properties/${getters.property.id}/property-group`);
      return response.data;
    } catch (error) {
      return false;
    }
  },
  async updatePropertyGroup({ commit }, propertyGroupId) {
    try {
      commit('beforeRequest');
      await ApiService.put(`${propertyEndpoint}/master-data/groups`, { propertyGroupId });
      commit('requestComplete');
      return true;
    } catch (error) {
      commit('requestComplete');
      handleApiError(commit, error);
      return false;
    }
  },
  async removeProperty({ commit }, propertyIsDeletedProxy) {
    commit('beforePropertiesRequest');
    try {
      await ApiService.put(`${endpoint}/properties/delete`, { isDeleted: propertyIsDeletedProxy });
      commit('updatePropertyIsDeleted', !propertyIsDeletedProxy);
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async updateStaticData({ commit }, data) {
    commit('beforeRequest');
    try {
      await ApiService.post(`${propertyEndpoint}/master-data/target-groups`, {
        add: data.add,
        delete: data.delete,
      });
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('requestComplete');
    }
    return null;
  },
  async getPropertyMainLanguage({ commit }) {
    try {
      const { data } = await ApiService.get(`${propertyEndpoint}/master-data/languages/main`);
      const { mainLanguage, supportedLanguages, autoTranslatedLanguages } = data;
      commit('setMainLanguage', { lang: mainLanguage, supportedLanguages, autoTranslatedLanguages });
    } catch (error) {
      commit('appLoading', false, { root: true });
      handleApiError(commit, error);
    }
  },
  async createPropertyInCultSwitch({ commit }, data) {
    try {
      return await ApiService.put(`${endpoint}/properties/create-in-cultswitch`, data);
    } catch (error) {
      handleApiError(commit, error);
      return false;
    }
  },
  async getPropertyForTesting({ commit, getters }) {
    commit('beforePropertiesRequest');
    try {
      const { data } = await ApiService.get(`${endpoint}/properties/${getters.property.id}/forTesting`);
      commit('updateRoomDBForTesting', !!data);
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async updatePropertyForTesting({ commit, getters }, dataDB) {
    commit('beforePropertiesRequest');
    try {
      const { id } = dataDB;
      await ApiService.put(`${endpoint}/properties/${id || getters.property.id}/forTesting`, dataDB);
      commit('updateRoomDBForTesting', dataDB.active);
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async updatePropertyIsDeleted({ commit }, dataDB) {
    commit('beforePropertiesRequest');
    try {
      const { id } = dataDB;
      await ApiService.put(`${endpoint}/properties/${id}/isDeleted`, dataDB);
      commit('afterPropertiesRequest');
    } catch (error) {
      commit('afterPropertiesRequest');
      handleApiError(commit, error);
    }
  },
  async getUserProperties({ commit }, payload) {
    const page = payload?.page || 1;
    const perPage = payload?.perPage || 10;
    const filter = payload?.filter ? payload.filter : '';
    const order = payload?.order ? payload.order : 'id';
    const sort = payload?.sort ? payload.sort : 'desc';
    const createdFrom = payload?.createdFrom ?? '';
    const createdTo = payload?.createdTo ?? '';
    const updatedFrom = payload?.updatedFrom ?? '';
    const updatedTo = payload?.updatedTo ?? '';
    const search = payload?.search ?? '';
    const searchKey = payload?.searchKey ?? '';
    const { type } = payload;
    const params = {
      page,
      perPage,
      filter,
      order,
      sort,
      createdFrom,
      createdTo,
      updatedFrom,
      updatedTo,
      search,
      searchKey,
    };
    try {
      commit('beforeUserProperties');
      const { data } = await ApiService.post(`${endpoint}/properties/${type}/all`, params);
      commit('afterUserProperties');
      return data;
    } catch (error) {
      commit('afterUserProperties');
      handleApiError(commit, error);
      return {};
    }
  },
  updateStatus({ commit }, active) {
    commit('updatePropertyStatus', active);
  },
  async updateTypesenseData({ commit }, payload) {
    commit('beforeTypesenseUpdate');
    try {
      await ApiService.post(`${endpoint}/properties/update-typesense`, payload);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('afterTypesenseUpdate');
    }
  },
  async updateAllTypesenseData({ commit }) {
    commit('beforeTypesenseUpdate');
    try {
      await ApiService.post(`${endpoint}/properties/update-all-typesense`);
    } catch (error) {
      handleApiError(commit, error);
    } finally {
      commit('afterTypesenseUpdate');
    }
  },
  async getUserFollowers({ commit }) {
    commit('beforeUserFollowers');
    try {
      const { data } = await ApiService.get(`${endpointUserAccount}/follower/followers`);
      const followers = data?.data?.result?.content ?? [];
      commit('afterUserFollowers', followers);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async getUserFollowees({ commit }) {
    commit('beforeUserFollowees');
    try {
      const { data } = await ApiService.get(`${endpointUserAccount}/follower/followees`);
      const followers = data?.data?.result?.content ?? [];
      commit('afterUserFollowees', followers);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async createFollower({ commit }, payload) {
    commit('beforeCreateFollower');
    try {
      const { data } = await ApiService.post(`${endpointUserAccount}/follower`, payload);
      commit('afterCreateFollower', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async deleteFollower({ commit }, payload) {
    commit('beforeRequest');
    try {
      const { data } = await ApiService.delete(`${endpointUserAccount}/follower/${payload.email}`);
      commit('requestComplete', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async deleteFollowee({ commit }, payload) {
    commit('beforeRequest');
    try {
      const { data } = await ApiService.delete(`${endpointUserAccount}/follower/followees/${payload.email}`);
      commit('requestComplete', data);
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async updateWebsiteUrlReachableStatus({ commit }, payload) {
    try {
      const { data } = await ApiService.post(`${propertyEndpoint}/master-data/info/check-website-url-reachable`, payload);
      commit('updatePropertyObject', {
        item: 'websiteUrlReachable',
        value: data.websiteUrlReachable,
      });
      commit('updatePropertyObject', {
        item: 'websiteUrlReachabilityLastUpdate',
        value: data.websiteUrlReachabilityLastUpdate,
      });
      commit('updatePropertyObject', {
        item: 'website',
        value: data.website,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
  async updateUserPropertySetting({ commit }, payload) {
    try {
      commit('setUpdateUserPropertySettingRequest', true);

      const { data } = await ApiService.post(`${endpoint}/property-setting`, payload);
      commit('updateLandingPropertyObject', { item: 'settings', value: data });
      commit('setUpdateUserPropertySettingRequest', false);
    } catch (error) {
      //
    }
  },
  getUserPropertySetting({ commit }) {
    try {
      // eslint-disable-next-line promise/always-return,promise/catch-or-return,promise/prefer-await-to-then
      ApiService.get(`${endpoint}/property-setting`).then((response) => {
        const { data } = response;
        commit('updateLandingPropertyObject', { item: 'settings', value: data });
      });
    } catch (error) {
      //
    }
  },
  async getOffers({ commit }) {
    try {
      commit('beforeOffersData');
      const { data } = await ApiService.get(`${endpointUserAccount}/offers`);
      commit('afterOffersData', data);
    } catch (error) {
      //
    }
  },
  async createOffer({ commit }, payload) {
    commit('beforeRequest');
    try {
      await ApiService.post(`${endpointUserAccount}/offers`, payload);
      commit('requestComplete');
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async editOffer({ commit }, { id, payload }) {
    commit('beforeRequest');
    try {
      await ApiService.put(`${endpointUserAccount}/offers/${id}`, payload);
      commit('requestComplete');
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async deleteOffer({ commit }, id) {
    commit('beforeRequest');
    try {
      await ApiService.delete(`${endpointUserAccount}/offers/${id}`);
      commit('requestComplete');
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async acceptOffer({ commit }, { id, payload }) {
    commit('beforeRequest');
    try {
      const formData = new FormData();
      formData.append('acceptedAt', payload.acceptedAt);
      formData.append('acceptedBy', payload.acceptedBy);
      formData.append('contract', payload.contract);
      await ApiService.post(`${endpointUserAccount}/offers/${id}/accept`, formData);
      commit('requestComplete');
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async sendEmail({ commit }, { id, payload }) {
    commit('beforeRequest');
    try {
      await ApiService.post(`${endpointUserAccount}/offers/${id}/send-email`, { emailConfig: payload });
      commit('requestComplete');
    } catch (error) {
      handleApiError(commit, error);
    }
  },
  async getPropertyGroupByUrl({ commit }) {
    try {
      const { data } = await ApiService.get(`${endpoint}/property-group`);
      const currentProperty = {
        propertyGroup: data,
      };
      commit('currentProperty', currentProperty);
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
